import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, Upload, Trash2, Loader2, TrendingUp, TrendingDown, Receipt, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Gate({ onOk }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/dashboard/verify", { code });
      if (data.ok) { localStorage.setItem("dash_code", code); onOk(); }
      else { setErr("Wrong code."); setCode(""); }
    } catch (_) { setErr("Something went wrong."); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="dashboard-gate" className="w-full max-w-xs border border-neon-green/40 bg-elevated p-8 text-center">
        <Lock size={28} className="text-neon-green mx-auto" />
        <h1 className="font-heading text-3xl text-neon-green uppercase tracking-wide mt-3">Owner Dashboard</h1>
        <p className="font-body text-sm text-neutral-400 mt-1">Enter your access code.</p>
        <input
          autoFocus type="tel" inputMode="numeric" value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setErr(""); }}
          data-testid="dashboard-code-input" placeholder="••••••"
          className="mt-5 w-full bg-black border border-neon-green/30 text-white text-center text-2xl tracking-[0.4em] py-3 focus:outline-none focus:border-neon-green"
        />
        {err && <p className="font-body text-sm text-red-500 mt-2" data-testid="dashboard-code-error">{err}</p>}
        <Button type="submit" disabled={loading} data-testid="dashboard-code-submit" className="mt-5 w-full font-heading text-xl bg-neon-green text-black hover:bg-neon-yellow py-5">
          {loading ? <Loader2 className="animate-spin" size={20} /> : "Enter"}
        </Button>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem("dash_code"));
  const [month, setMonth] = useState(thisMonth());
  const [pnl, setPnl] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        api.get(`/dashboard/pnl?month=${month}`),
        api.get(`/dashboard/expenses?month=${month}`),
      ]);
      setPnl(p.data); setExpenses(e.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("dash_code"); setAuthed(false); }
      else toast.error("Could not load dashboard");
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/dashboard/expenses", { image_base64: b64 });
      toast.success(`Added: ${data.vendor} · ${money(data.amount_cents)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    setExpenses((p) => p.filter((x) => x._id !== id));
    try { await api.delete(`/dashboard/expenses/${id}`); load(); } catch (_) { load(); }
  };

  if (!authed) return <Gate onOk={() => setAuthed(true)} />;

  const net = pnl?.net_cents ?? 0;
  const profit = net >= 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-neon-green/20 px-5 sm:px-8 h-[64px] flex items-center justify-between">
        <button onClick={() => navigate("/kds")} data-testid="dashboard-back" className="flex items-center gap-2 text-neutral-300 hover:text-neon-green transition-colors">
          <ArrowLeft size={20} /> <span className="font-heading text-xl uppercase">Kitchen</span>
        </button>
        <span className="font-heading text-2xl text-neon-green">67 <span className="text-neon-yellow">BOOKS</span></span>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/dashboard/menu")} data-testid="dashboard-menu-link" className="inline-flex items-center gap-1.5 border border-neon-green/40 text-neon-green px-3 py-1.5 hover:bg-neon-green hover:text-black transition-colors font-heading text-sm uppercase tracking-wide">
            <UtensilsCrossed size={14} /> Menu
          </button>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="dashboard-month" className="bg-elevated border border-neon-green/30 text-white text-sm px-2 py-1" />
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-8">
        <h1 className="font-heading text-5xl sm:text-6xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green">{pnl?.label || month}</h1>
        <p className="font-body text-sm text-neutral-500 mt-1">Profit & Loss · online + kiosk sales vs. your receipts</p>

        {loading && !pnl ? (
          <div className="py-20 text-center text-neutral-500"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" data-testid="pnl-cards">
              <div className="border border-neon-green/25 bg-elevated p-6">
                <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-400">Revenue</span>
                <div className="font-heading text-4xl text-neon-green mt-1" data-testid="pnl-revenue">{money(pnl?.revenue_cents)}</div>
                <span className="font-body text-xs text-neutral-500">{pnl?.order_count || 0} paid orders</span>
              </div>
              <div className="border border-neon-yellow/25 bg-elevated p-6">
                <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-400">Expenses</span>
                <div className="font-heading text-4xl text-neon-yellow mt-1" data-testid="pnl-expenses">{money(pnl?.expenses_cents)}</div>
                <span className="font-body text-xs text-neutral-500">{expenses.length} receipts</span>
              </div>
              <div className={`border p-6 ${profit ? "border-neon-green/40" : "border-red-500/50"}`}>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-400">Net {profit ? "Profit" : "Loss"}</span>
                <div className={`font-heading text-4xl mt-1 flex items-center gap-2 ${profit ? "text-neon-green" : "text-red-500"}`} data-testid="pnl-net">
                  {profit ? <TrendingUp size={26} /> : <TrendingDown size={26} />}{money(Math.abs(net))}
                </div>
              </div>
            </div>

            {pnl?.summary && (
              <div className="mt-4 border border-neon-green/20 bg-elevated p-5" data-testid="pnl-summary">
                <span className="font-heading text-lg text-neon-green uppercase tracking-wide">AI Summary</span>
                <p className="font-body text-neutral-300 mt-2 whitespace-pre-line">{pnl.summary}</p>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <h2 className="font-heading text-3xl text-neon-yellow uppercase tracking-wide">Receipts</h2>
              <div>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="receipt-file-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-receipt-btn" className="font-heading text-lg bg-neon-green text-black hover:bg-neon-yellow">
                  {uploading ? <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Reading…</span> : <span className="flex items-center gap-2"><Upload size={18} /> Add Receipt</span>}
                </Button>
              </div>
            </div>
            <p className="font-body text-xs text-neutral-500 mt-1">Snap a photo of a receipt — AI reads the vendor, amount & category automatically.</p>

            <div className="mt-4 space-y-2" data-testid="expense-list">
              {expenses.length === 0 && <p className="font-body text-neutral-600 py-8 text-center">No receipts yet this month.</p>}
              {expenses.map((x) => (
                <motion.div key={x._id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="expense-row" className="flex items-center gap-4 border border-neon-green/15 bg-elevated p-4">
                  <Receipt size={20} className="text-neon-green shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg text-white uppercase tracking-wide truncate">{x.vendor}</div>
                    <div className="font-body text-xs text-neutral-500">{x.category}{x.date ? ` · ${x.date}` : ""}</div>
                  </div>
                  <span className="font-heading text-xl text-neon-yellow">{money(x.amount_cents)}</span>
                  <button onClick={() => del(x._id)} data-testid="expense-delete" className="text-neutral-600 hover:text-red-500"><Trash2 size={18} /></button>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
